import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/admin/settings');
      console.log('Settings API Response:', response.data);
      const resData = response.data;
      
      let data = resData.data || resData;
      if (data && data.settings && !Array.isArray(data)) {
        data = data.settings;
      }
      
      const actualSettings = Array.isArray(data) 
        ? data.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {})
        : (data.data && Array.isArray(data.data) 
            ? data.data.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {})
            : data);

      setSettings(actualSettings);
    } catch (err) {
      setError('Failed to load application settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/api/admin/settings', { settings });
      setSuccess('All settings have been successfully updated.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('An error occurred while saving settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <RefreshCw className="w-8 h-8 animate-spin mb-2" />
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Application Configuration</h1>
        <p className="text-gray-500 text-sm">Fine-tune your platform's behavior and global variables.</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-3" />
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 mr-3" />
          {success}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <form onSubmit={handleUpdate} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            {Object.keys(settings).length === 0 ? (
              <div className="text-center py-8 text-gray-400 italic">
                No configurable settings found in the system.
              </div>
            ) : (
              Object.entries(settings).map(([key, value]) => (
                <div key={key} className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 capitalize">
                    {key.replace(/_/g, ' ')}
                  </label>
                  <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all"
                    placeholder={`Enter ${key.replace(/_/g, ' ')}`}
                  />
                </div>
              ))
            )}
          </div>

          <div className="pt-4 border-t border-gray-100 flex justify-end">
            <button
              type="submit"
              disabled={saving || Object.keys(settings).length === 0}
              className="flex items-center px-8 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-50 transition-all shadow-lg shadow-blue-100"
            >
              {saving ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
              {saving ? 'Updating...' : 'Save All Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
